import { UNIT_LABEL_FALLBACKS } from "../constants/products";
import { getUnitLabel } from "../../../Utils/productValidation";
import { toGraphQLBaseUnit } from "../../../Utils/toGraphQLBaseUnit";
import { ProductItemStatus } from "../enums/productItems";
import { SellingMode } from "../enums/sellingMode";
import { Unit } from "../enums/unit";
import {
  Product,
  ProductInventoryFilterKey,
  TranslateFn
} from "../types/products";

const STATUS_VERB_FALLBACKS: Record<ProductItemStatus, string> = {
  [ProductItemStatus.CREATED]: "created",
  [ProductItemStatus.IN_STOCK]: "in stock",
  [ProductItemStatus.RESERVED]: "reserved",
  [ProductItemStatus.SOLD]: "sold",
  [ProductItemStatus.DAMAGED]: "damaged",
  [ProductItemStatus.EXPIRED]: "expired",
  [ProductItemStatus.LOST]: "lost",
  [ProductItemStatus.CONSUMED]: "consumed"
};

export function getProductUnitLabel(product: Product, t: TranslateFn): string {
  const wire = toGraphQLBaseUnit(
    product.baseUnit ?? (product.unit as string | undefined)
  );
  if (wire && (Object.values(Unit) as string[]).includes(wire)) {
    return getUnitLabel(wire as Unit, t);
  }
  return (
    product.baseUnit ??
    product.unit ??
    t("units-unit", UNIT_LABEL_FALLBACKS.UNIT)
  );
}

export function getInventoryQuantityPrefix(
  sellingMode: SellingMode,
  unitLabel: string,
  t: TranslateFn
): string {
  switch (sellingMode) {
    case SellingMode.SINGLE_ITEM:
      return t("product-inventory-prefix-items", "items");
    case SellingMode.UNIT_BASED:
      return t("product-inventory-prefix-units", "units");
    case SellingMode.MEASURED:
      return unitLabel;
    default:
      return t("product-inventory-prefix-units", "units");
  }
}

/** Label for per-item stock quantity fields in the add-item wizard. */
export function getPerItemQuantityFieldLabel(
  sellingMode: SellingMode,
  unitLabel: string,
  t: TranslateFn
): string {
  switch (sellingMode) {
    case SellingMode.SINGLE_ITEM:
      return t("add-product-item-quantity-label", "Quantity");
    case SellingMode.UNIT_BASED:
      return t("add-product-item-units-in-each-item", "Units in each item");
    case SellingMode.MEASURED: {
      const translated = t(
        "add-product-item-measured-in-each-item",
        `${unitLabel} in each item`,
        { unit: unitLabel }
      );
      if (/\{\{?unit\}?\}/.test(translated)) {
        return `${unitLabel} in each item`;
      }
      return translated;
    }
    default:
      return t("add-product-item-quantity-label", "Quantity");
  }
}

/** Suffix for price fields, e.g. "per item", "per unit", "per liters". */
export function getPricePerUnitSuffix(
  sellingMode: SellingMode,
  unitLabel: string,
  t: TranslateFn
): string {
  switch (sellingMode) {
    case SellingMode.SINGLE_ITEM:
      return t("product-item-price-per-item", "per item");
    case SellingMode.UNIT_BASED:
      return t("product-item-price-per-unit", "per unit");
    case SellingMode.MEASURED: {
      const translated = t(
        "product-item-price-per-measure",
        `per ${unitLabel}`,
        { unit: unitLabel }
      );
      if (/\{\{?unit\}?\}/.test(translated)) {
        return `per ${unitLabel}`;
      }
      return translated;
    }
    default:
      return t("product-item-price-per-unit", "per unit");
  }
}

export function getProductInventoryStatCardDescription(
  filterKey: ProductInventoryFilterKey,
  sellingMode: SellingMode,
  unitLabel: string,
  t: TranslateFn
): string {
  const prefix = getInventoryQuantityPrefix(sellingMode, unitLabel, t);
  const status = filterKey as ProductItemStatus;
  const verb = STATUS_VERB_FALLBACKS[status];

  if (status === ProductItemStatus.IN_STOCK) {
    return t("product-inventory-desc-in-stock", `${prefix} in stock`, {
      prefix
    });
  }

  return t(
    `product-inventory-desc-${status.toLowerCase()}`,
    `${verb} ${prefix}`,
    { prefix }
  );
}

/** Formats a quantity with the selling-mode unit prefix (e.g. "100 units", "50 liters"). */
export function formatProductItemQuantityWithUnit(
  quantity: number | undefined | null,
  sellingMode: SellingMode,
  unitLabel: string,
  t: TranslateFn
): string {
  const prefix = getInventoryQuantityPrefix(sellingMode, unitLabel, t);
  const count = (quantity ?? 0).toLocaleString();
  return t("product-item-quantity-with-unit", `${count} ${prefix}`, {
    count,
    unit: prefix
  });
}
